import { DrugDomainService } from './domain';
import { DrugFactory } from './entity/drugFactory';

import type { DrugIRepository } from './entity/drugIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { DrugJson, DrugJsonPvt } from '@/types/domain/drug/entities/drugEntity';

/**
 * 薬アプリケーションサービス
 */
export class DrugApplicationService {
  private service!: DrugDomainService;

  private drugFactory = new DrugFactory();

  /**
   * Injection
   * @param drugRepository drugRepository
   * @param logger logger
   */
  public inject(
    drugRepository: DrugIRepository,
    logger: Logger,
  ) {
    this.service = new DrugDomainService(logger);
    this.service.inject(drugRepository);
  }

  /**
   * 薬リスト取得
   * @param token トークン
   * @returns リスト
   */
  async getDrugList(token: string | null) {
    const result = await this.service.getDrugList(token);
    return result;
  }

  /**
   * 指定日の薬リスト取得
   * @param date 日付
   * @returns リスト
   */
  async getDrugListWithDate(date: string) {
    const result = await this.service.getDrugListWithDate(date);
    return result;
  }

  /**
   * 薬作成
   * @param input 入力
   * @returns 結果
   */
  async createDrug(
    input: DrugJson,
  ) {
    const entity = this.drugFactory.createFromJson(input);
    const result = await this.service.createDrug(entity);
    return result;
  }

  /**
   * 薬更新
   * @param input 入力
   * @returns 結果
   */
  async updateDrug(
    input: DrugJson,
  ) {
    const entity = this.drugFactory.createFromJson(input);
    const result = await this.service.updateDrug(entity);
    return result;
  }

  /**
   * 薬削除
   * @param input 入力
   * @returns 結果
   */
  async deleteDrug(
    input: DrugJson,
  ) {
    const entity = this.drugFactory.createFromJson(input);
    const result = await this.service.deleteDrug(entity);
    return result;
  }

  /**
   * 薬リストを更新する(Create)
   * @param drugList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createDrugStore(
    drugList: DrugJson[],
    payload: EntitySubscribeType,
    filter: ((json: DrugJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      drugList,
      payload,
      this.drugFactory,
      filter,
    );
  }

  /**
   * 薬リストを更新する(Update)
   * @param drugList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateDrugStore(
    drugList: DrugJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      drugList,
      payload,
      this.drugFactory,
    );
  }

  /**
   * 薬リストを更新する(Delete)
   * @param drugList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteDrugStore(
    drugList: DrugJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      drugList,
      payload,
      this.drugFactory,
    );
  }
}
